import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Package, Calculator, FileText, Gift, User, ChevronLeft, ChevronRight, LogOut, Moon, Sun, Truck, Receipt, Building, ShoppingBag, Store } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, getUser } from '../../state/authSlice';
import { getIsPartnerLinked, getCourierSetting } from '../../state/courierSlice';
import { useDeliveryAvailability } from '../../hooks/useDeliveryAvailability';
import { useDarkMode } from '../../context/DarkModeContext';

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
}

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
}: SidebarProps) => {
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDeliveryAvailable } = useDeliveryAvailability();
  const user = useSelector(getUser);
  const isPartnerLinked = useSelector(getIsPartnerLinked);
  const courierSetting = useSelector(getCourierSetting);

  const navItems = [{
    name: user?.isCourier ? 'Dashboard' : 'Warehouse',
    path: '/warehouse',
    icon: <Building className="h-5 w-5" />,
    show: user?.isCourier === true && isPartnerLinked
  }, {
    name: 'Mailbox',
    path: '/mailbox',
    icon: <Mail className="h-5 w-5" />
    // Shown for everyone: customers see their own address; warehouse couriers
    // use it to advise their customers of the shipping-address format.
  }, {
    name: 'Packages',
    path: '/packages',
    icon: <Package className="h-5 w-5" />
  }, {
    name: 'Invoices',
    path: '/invoices',
    icon: <Receipt className="h-5 w-5" />,
    show: isPartnerLinked
  }, {
    name: 'Delivery Request',
    path: '/delivery-request',
    icon: <Truck className="h-5 w-5" />,
    show: isDeliveryAvailable && user?.isCourier !== true
  }, {
    name: 'Local Delivery',
    path: '/local-delivery',
    icon: <Truck className="h-5 w-5" />,
    show: courierSetting?.isLocalDeliveryEnabled === true && user?.isCourier !== true
  }, {
    name: 'Shop For Me',
    path: '/shop-for-me',
    icon: <ShoppingBag className="h-5 w-5" />,
    show: courierSetting?.isShopForMeEnabled === true && user?.isCourier !== true
  }, {
    name: 'Shop',
    path: '/shop',
    icon: <Store className="h-5 w-5" />,
    show: courierSetting?.isShopEnabled === true && user?.isCourier !== true
  }, {
    name: 'Calculator',
    path: '/calculator',
    icon: <Calculator className="h-5 w-5" />,
    show: user?.isCourier !== true
  },
  {
    name: 'Authorized Users',
    path: '/authorized-users',
    icon: <User className="h-5 w-5" />,
    show: user?.isCourier !== true
  },
  {
    name: 'Referrals & Rewards',
    path: '/referrals',
    icon: <Gift className="h-5 w-5" />,
    show: user?.isCourier !== true
  }, {
    name: 'Profile',
    path: '/profile',
    icon: <User className="h-5 w-5" />,
    show: user?.isCourier !== true
  }];

  // Filter out items that shouldn't be shown
  const visibleNavItems = navItems.filter(item => item.show !== false);

  const dispatch = useDispatch();
  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  return <div className={`h-full transition-all duration-300 flex flex-col ${
    isDarkMode
      ? 'bg-gray-900 text-white'
      : 'bg-primary text-white'
  } ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
      <div className="flex items-center justify-between p-4">
        {isSidebarOpen && <div className="text-xl font-bold">Dashboard</div>}
        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={`p-1 rounded-full focus:outline-none ${
          isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white/20'
        }`}>
          {isSidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
      </div>
      <div className="mt-6 flex-1">
        {visibleNavItems.map(item => {
        const isActive = location.pathname === item.path;
        return <Link key={item.path} to={item.path} className={`
                flex items-center px-4 py-3 transition-colors
                ${isActive ? (isDarkMode ? 'bg-gray-700' : 'bg-white/20') : (isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white/20')}
                ${isSidebarOpen ? 'justify-start' : 'justify-center'}
              `}>
              <div className="flex items-center">
                {item.icon}
                {isSidebarOpen && <span className="ml-3">{item.name}</span>}
              </div>
            </Link>;
      })}
      </div>
      {/* Dark mode toggle */}
      <div className="px-4 py-3">
        <button onClick={toggleDarkMode} className={`
            flex items-center transition-colors w-full rounded-md p-2
            ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white/20'}
            ${isSidebarOpen ? 'justify-start' : 'justify-center'}
          `}>
          {isDarkMode ? <Sun className="h-5 w-5 text-yellow-300" /> : <Moon className="h-5 w-5 text-blue-300" />}
          {isSidebarOpen && <span className="ml-3">
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </span>}
        </button>
      </div>
      {/* Logout button at bottom */}
      <div className="mb-6">
        <button onClick={handleLogout} className={`
            flex items-center px-4 py-3 transition-colors w-full
            ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white/20'}
            ${isSidebarOpen ? 'justify-start' : 'justify-center'}
          `}>
          <LogOut className="h-5 w-5 text-red-400" />
          {isSidebarOpen && <span className="ml-3">Logout</span>}
        </button>
      </div>
    </div>;
};
export default Sidebar;
